import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  map,
  switchMap,
  withLatestFrom
} from 'rxjs/operators';

import { OptimizationService } from '@core/services/optimization/optimization.service';
import * as PlanActions from '@core/store/plan/plan.actions';

import { OptimizationHistoryActions } from './optimization-history.actions';
import {
  selectOptimizationHistoryState,
  selectPageOpen
} from './optimization-history.selectors';
import {
  isNewOutputPlanOnPage,
  isUnseenMatchingCreation
} from './optimization-history.state';

export const RELOAD_DEBOUNCE_MS = 1000;

@Injectable()
export class OptimizationHistoryEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly service = inject(OptimizationService);

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OptimizationHistoryActions.load),
      switchMap(({ query }) =>
        this.service.listHistory$(query).pipe(
          map((page) => OptimizationHistoryActions.loadSuccess({ page })),
          catchError(() => of(OptimizationHistoryActions.loadFailure()))
        )
      )
    )
  );

  // Events carry ids but no names, so what they add is fetched rather than shown unnamed: a new
  // job on the first page (later pages announce it instead), and a loaded row's output plan.
  // The slice is root-level and the event is app-wide, so events are dropped unless the page is open.
  reloadForMissingNames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PlanActions.optimizationUpdated),
      withLatestFrom(
        this.store.select(selectOptimizationHistoryState),
        this.store.select(selectPageOpen)
      ),
      filter(
        ([{ result }, state, pageOpen]) =>
          pageOpen &&
          ((state.query.page === 1 && isUnseenMatchingCreation(state, result)) ||
            isNewOutputPlanOnPage(state, result))
      ),
      debounceTime(RELOAD_DEBOUNCE_MS),
      withLatestFrom(this.store.select(selectOptimizationHistoryState)),
      map(([, state]) => OptimizationHistoryActions.load({ query: state.query }))
    )
  );
}
